import {
  DesiredDesignState,
  DesiredForgeDesignGroup,
  DesiredItemState,
  ForgeDesignNote,
  ForgeDesignGroupScope,
  MasterLabelDebugInfo,
  MasterLabelDiffSummary,
  MasterLabelReviewIssueType,
  MasterLabelStateSaveRequest,
  MasterUPCData,
} from '../types';

type CropSection = 'design' | 'pending' | 'no-design' | 'carton' | 'inactive';

interface CropPlacement {
  cropId: string;
  itemNumber: string;
  section: CropSection;
  designId: string | null;
  orientation: string | null;
  reviewIssueType: MasterLabelReviewIssueType | null;
  markedForDeletion: boolean;
}

interface DesignSnapshot {
  id: string;
  persistedDesignId: string | null;
  itemNumber: string;
  representativeCropId: string | null;
  notesKey: string;
  cropIds?: string[];
}

interface ItemDiffAccumulator {
  itemNumber: string;
  cropChangeCount: number;
  representativeChangeCount: number;
  designNoteChangeCount: number;
  draftDesignCount: number;
  designGroupChangeCount: number;
  dirtyCropIds: Set<string>;
  dirtyDesignIds: Set<string>;
}

function createAccumulator(itemNumber: string): ItemDiffAccumulator {
  return {
    itemNumber,
    cropChangeCount: 0,
    representativeChangeCount: 0,
    designNoteChangeCount: 0,
    draftDesignCount: 0,
    designGroupChangeCount: 0,
    dirtyCropIds: new Set(),
    dirtyDesignIds: new Set(),
  };
}

function ensureAccumulator(
  accByItem: Map<string, ItemDiffAccumulator>,
  itemNumber: string
): ItemDiffAccumulator {
  let acc = accByItem.get(itemNumber);
  if (!acc) {
    acc = createAccumulator(itemNumber);
    accByItem.set(itemNumber, acc);
  }
  return acc;
}

function sortIds(ids: string[]): string[] {
  return [...ids].sort((a, b) => a.localeCompare(b));
}

function sortUniqueIds(ids: string[]): string[] {
  return sortIds([...new Set(ids.filter(Boolean))]);
}

function isMongoObjectId(value?: string | null): boolean {
  return Boolean(value && /^[a-f\d]{24}$/i.test(value));
}

function getSerializableDesignId(design: MasterUPCData['designs'][number]): string | null {
  return design.persistedDesignId || (isMongoObjectId(design.id) ? design.id : null);
}

function getSerializableDesignNotes(design: MasterUPCData['designs'][number]): ForgeDesignNote[] {
  return (design.notes ?? []).filter((note) => note.text.trim().length > 0);
}

function serializeNotesKey(design: MasterUPCData['designs'][number]): string {
  return JSON.stringify(
    getSerializableDesignNotes(design)
      .map((note) => ({
        id: note.id ?? null,
        text: note.text,
        createdBy: note.createdBy ?? null,
        createdAt: note.createdAt ?? null,
      }))
      .filter((note) => note.text.trim().length > 0)
  );
}

function isDesignPendingDeletion(design: MasterUPCData['designs'][number]): boolean {
  return design.crops.length > 0 && design.crops.every((crop) => crop.markedForDeletion);
}

function collectPendingDeletedDesignIds(upcs: MasterUPCData[]): Set<string> {
  const designIds = new Set<string>();
  for (const upc of upcs) {
    for (const design of upc.designs) {
      if (!isDesignPendingDeletion(design)) continue;
      designIds.add(design.id);
      if (design.persistedDesignId) {
        designIds.add(design.persistedDesignId);
      }
    }
  }
  return designIds;
}

function normalizeDesignId(designId?: string | null): string | null {
  return designId ?? null;
}

function normalizeOrientation(orientation?: string): string | null {
  return orientation ?? null;
}

function cropPlacementKey(placement: CropPlacement | undefined): string {
  if (!placement) return '__missing__';
  return [
    placement.itemNumber,
    placement.section,
    placement.designId ?? '',
    placement.orientation ?? '',
    placement.reviewIssueType ?? '',
    placement.markedForDeletion ? 'delete' : '',
  ].join('|');
}

function flattenCropPlacements(upcs: MasterUPCData[]): Map<string, CropPlacement> {
  const placements = new Map<string, CropPlacement>();

  for (const upc of upcs) {
    for (const design of upc.designs) {
      for (const crop of design.crops) {
        placements.set(crop.id, {
          cropId: crop.id,
          itemNumber: upc.itemNumber,
          section: 'design',
          designId: normalizeDesignId(design.id),
          orientation: normalizeOrientation(crop.orientation),
          reviewIssueType: crop.reviewIssueType ?? null,
          markedForDeletion: Boolean(crop.markedForDeletion),
        });
      }
    }
    for (const crop of upc.pendingCrops ?? []) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'pending',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
    for (const crop of upc.unassignedCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'no-design',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
    for (const crop of upc.cartonCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'carton',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
    for (const crop of upc.inactiveCrops ?? []) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'inactive',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
  }

  return placements;
}

function getAllUpcCrops(upc: MasterUPCData) {
  return [
    ...upc.designs.flatMap((design) => design.crops),
    ...(upc.pendingCrops ?? []),
    ...upc.unassignedCrops,
    ...upc.cartonCrops,
    ...(upc.inactiveCrops ?? []),
  ];
}

function sortReviewCropIds(cropIds: string[]): string[] {
  return [...cropIds].sort((left, right) => left.localeCompare(right));
}

function buildReviewState(upc: MasterUPCData) {
  const missingDesignCropIds: string[] = [];
  const sopUnclearCropIds: string[] = [];
  const badQualityCropIds: string[] = [];
  const markForDeletionCropIds: string[] = [];
  const otherCropIds: string[] = [];

  for (const crop of getAllUpcCrops(upc)) {
    if (crop.reviewIssueType === 'missing_design') {
      missingDesignCropIds.push(crop.id);
    } else if (crop.reviewIssueType === 'sop_unclear') {
      sopUnclearCropIds.push(crop.id);
    } else if (crop.reviewIssueType === 'bad_quality') {
      badQualityCropIds.push(crop.id);
    } else if (crop.reviewIssueType === 'mark_for_deletion') {
      markForDeletionCropIds.push(crop.id);
    } else if (crop.reviewIssueType === 'other') {
      otherCropIds.push(crop.id);
    }
  }

  return {
    missingDesignCropIds: sortReviewCropIds(missingDesignCropIds),
    sopUnclearCropIds: sortReviewCropIds(sopUnclearCropIds),
    badQualityCropIds: sortReviewCropIds(badQualityCropIds),
    markForDeletionCropIds: sortReviewCropIds(markForDeletionCropIds),
    otherCropIds: sortReviewCropIds(otherCropIds),
  };
}

function flattenPersistedDesigns(upcs: MasterUPCData[]): Map<string, DesignSnapshot> {
  const designs = new Map<string, DesignSnapshot>();
  for (const upc of upcs) {
    for (const design of upc.designs) {
      const persistedDesignId = getSerializableDesignId(design);
      if (!persistedDesignId) continue;
      designs.set(persistedDesignId, {
        id: design.id,
        persistedDesignId,
        itemNumber: upc.itemNumber,
        representativeCropId: design.representativeCropId,
        notesKey: serializeNotesKey(design),
      });
    }
  }
  return designs;
}

function flattenDraftDesigns(upcs: MasterUPCData[]): DesignSnapshot[] {
  const drafts: DesignSnapshot[] = [];
  for (const upc of upcs) {
    for (const design of upc.designs) {
      if (design.persistedDesignId) continue;
      drafts.push({
        id: design.id,
        persistedDesignId: null,
        itemNumber: upc.itemNumber,
        representativeCropId: design.representativeCropId,
        notesKey: serializeNotesKey(design),
        cropIds: sortIds(design.crops.map((crop) => crop.id)),
      });
    }
  }
  return drafts;
}

function groupMemberIdsKey(
  groupDesigns: MasterUPCData['designs'][number]['fullGroupDesigns']
): string {
  return sortUniqueIds((groupDesigns ?? []).map((member) => member.designId)).join('|');
}

function designGroupsKey(design: MasterUPCData['designs'][number]): string {
  return [
    groupMemberIdsKey(design.fullGroupDesigns),
    groupMemberIdsKey(design.partialGroupDesigns),
  ].join('::');
}

function formatDesignGroupForDebug(
  member: NonNullable<MasterUPCData['designs'][number]['fullGroupDesigns']>[number]
): string {
  return member.normalizedBarcode
    ? `${member.normalizedBarcode} · ${member.designId}`
    : member.designId;
}

function formatDesignGroupsForDebug(
  groupDesigns: MasterUPCData['designs'][number]['fullGroupDesigns']
): string[] {
  return [...(groupDesigns ?? [])]
    .map(formatDesignGroupForDebug)
    .sort((left, right) => left.localeCompare(right));
}

function findPersistedDesign(
  upcs: MasterUPCData[],
  itemNumber: string,
  persistedDesignId: string
): MasterUPCData['designs'][number] | undefined {
  return upcs
    .find((upc) => upc.itemNumber === itemNumber)
    ?.designs.find((design) => design.persistedDesignId === persistedDesignId);
}

function collectChangedDesignGroupIds(
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): string[] {
  const basePersistedDesigns = flattenPersistedDesigns(baseUpcs);
  const workingPersistedDesigns = flattenPersistedDesigns(workingUpcs);
  const changedDesignIds: string[] = [];
  const persistedDesignIds = new Set([
    ...basePersistedDesigns.keys(),
    ...workingPersistedDesigns.keys(),
  ]);

  for (const designId of persistedDesignIds) {
    const baseDesign = basePersistedDesigns.get(designId);
    const workingDesign = workingPersistedDesigns.get(designId);
    if (!baseDesign || !workingDesign) continue;

    const baseFullDesign = findPersistedDesign(baseUpcs, baseDesign.itemNumber, designId);
    const workingFullDesign = findPersistedDesign(workingUpcs, workingDesign.itemNumber, designId);
    if (!baseFullDesign || !workingFullDesign) continue;
    if (designGroupsKey(baseFullDesign) !== designGroupsKey(workingFullDesign)) {
      changedDesignIds.push(designId);
    }
  }

  return sortUniqueIds(changedDesignIds);
}

function finalizeSummary(
  accByItem: Map<string, ItemDiffAccumulator>,
  totalChanges: number
): MasterLabelDiffSummary {
  const itemSummaries = [...accByItem.values()]
    .map((acc) => ({
      itemNumber: acc.itemNumber,
      changeCount:
        acc.cropChangeCount +
        acc.representativeChangeCount +
        acc.designNoteChangeCount +
        acc.draftDesignCount +
        acc.designGroupChangeCount,
      cropChangeCount: acc.cropChangeCount,
      representativeChangeCount: acc.representativeChangeCount,
      designNoteChangeCount: acc.designNoteChangeCount,
      draftDesignCount: acc.draftDesignCount,
      designGroupChangeCount: acc.designGroupChangeCount,
      dirtyCropIds: sortIds([...acc.dirtyCropIds]),
      dirtyDesignIds: sortIds([...acc.dirtyDesignIds]),
    }))
    .filter((summary) => summary.changeCount > 0)
    .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber));

  return {
    totalChanges,
    dirtyItemNumbers: itemSummaries.map((summary) => summary.itemNumber),
    itemSummaries,
  };
}

export function computeMasterLabelDiffSummary(
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelDiffSummary {
  const accByItem = new Map<string, ItemDiffAccumulator>();
  const basePlacements = flattenCropPlacements(baseUpcs);
  const workingPlacements = flattenCropPlacements(workingUpcs);
  const cropIds = new Set([...basePlacements.keys(), ...workingPlacements.keys()]);

  let cropChangeTotal = 0;
  for (const cropId of cropIds) {
    const basePlacement = basePlacements.get(cropId);
    const workingPlacement = workingPlacements.get(cropId);
    if (cropPlacementKey(basePlacement) === cropPlacementKey(workingPlacement)) continue;
    cropChangeTotal += 1;
    for (const itemNumber of new Set(
      [basePlacement?.itemNumber, workingPlacement?.itemNumber].filter(Boolean) as string[]
    )) {
      const acc = ensureAccumulator(accByItem, itemNumber);
      acc.cropChangeCount += 1;
      acc.dirtyCropIds.add(cropId);
    }
  }

  const basePersistedDesigns = flattenPersistedDesigns(baseUpcs);
  const workingPersistedDesigns = flattenPersistedDesigns(workingUpcs);
  const persistedDesignIds = new Set([
    ...basePersistedDesigns.keys(),
    ...workingPersistedDesigns.keys(),
  ]);

  let representativeChangeTotal = 0;
  for (const designId of persistedDesignIds) {
    const baseDesign = basePersistedDesigns.get(designId);
    const workingDesign = workingPersistedDesigns.get(designId);

    if (!baseDesign || !workingDesign) continue;

    if (baseDesign.representativeCropId !== workingDesign.representativeCropId) {
      representativeChangeTotal += 1;
      for (const itemNumber of new Set([baseDesign.itemNumber, workingDesign.itemNumber])) {
        const acc = ensureAccumulator(accByItem, itemNumber);
        acc.representativeChangeCount += 1;
        acc.dirtyDesignIds.add(designId);
      }
    }
  }

  let designNoteChangeTotal = 0;
  for (const designId of persistedDesignIds) {
    const baseDesign = basePersistedDesigns.get(designId);
    const workingDesign = workingPersistedDesigns.get(designId);

    if (!baseDesign || !workingDesign) continue;

    if (baseDesign.notesKey !== workingDesign.notesKey) {
      designNoteChangeTotal += 1;
      for (const itemNumber of new Set([baseDesign.itemNumber, workingDesign.itemNumber])) {
        const acc = ensureAccumulator(accByItem, itemNumber);
        acc.designNoteChangeCount += 1;
        acc.dirtyDesignIds.add(designId);
      }
    }
  }

  const draftDesigns = flattenDraftDesigns(workingUpcs);
  let draftDesignTotal = 0;
  for (const draftDesign of draftDesigns) {
    draftDesignTotal += 1;
    const acc = ensureAccumulator(accByItem, draftDesign.itemNumber);
    acc.draftDesignCount += 1;
    acc.dirtyDesignIds.add(draftDesign.id);
  }

  const changedDesignGroupIds = collectChangedDesignGroupIds(baseUpcs, workingUpcs);
  for (const designId of changedDesignGroupIds) {
    const baseDesign = basePersistedDesigns.get(designId);
    const workingDesign = workingPersistedDesigns.get(designId);
    if (!baseDesign || !workingDesign) continue;
    for (const itemNumber of new Set([baseDesign.itemNumber, workingDesign.itemNumber])) {
      const acc = ensureAccumulator(accByItem, itemNumber);
      acc.designGroupChangeCount += 1;
      acc.dirtyDesignIds.add(designId);
    }
  }

  return finalizeSummary(
    accByItem,
    cropChangeTotal +
      representativeChangeTotal +
      designNoteChangeTotal +
      draftDesignTotal +
      changedDesignGroupIds.length
  );
}

function buildDesignGroupComponentsByScope(
  upcs: MasterUPCData[],
  scope: ForgeDesignGroupScope
): Set<string>[] {
  const adjacency = new Map<string, Set<string>>();
  const groupField = scope === 'full' ? 'fullGroupDesigns' : 'partialGroupDesigns';
  const excludedDesignIds = collectPendingDeletedDesignIds(upcs);

  const ensureNode = (designId: string) => {
    if (!adjacency.has(designId)) {
      adjacency.set(designId, new Set());
    }
  };

  for (const upc of upcs) {
    for (const design of upc.designs) {
      const sourceDesignId = getSerializableDesignId(design);
      if (sourceDesignId && excludedDesignIds.has(sourceDesignId)) continue;
      if (!sourceDesignId) continue;
      const groupDesigns = design[groupField] ?? [];
      for (const member of groupDesigns) {
        if (!member.designId || member.designId === sourceDesignId) continue;
        if (excludedDesignIds.has(member.designId)) continue;
        ensureNode(sourceDesignId);
        ensureNode(member.designId);
        adjacency.get(sourceDesignId)?.add(member.designId);
        adjacency.get(member.designId)?.add(sourceDesignId);
      }
    }
  }

  return connectedComponentsFromAdjacency(adjacency);
}

function connectedComponentsFromAdjacency(adjacency: Map<string, Set<string>>): Set<string>[] {
  const visited = new Set<string>();
  const components: Set<string>[] = [];
  for (const designId of sortIds([...adjacency.keys()])) {
    if (visited.has(designId)) continue;
    const stack = [designId];
    const component = new Set<string>();
    visited.add(designId);
    while (stack.length > 0) {
      const current = stack.pop() as string;
      component.add(current);
      for (const neighbor of adjacency.get(current) ?? []) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        stack.push(neighbor);
      }
    }
    if (component.size >= 2) components.push(component);
  }
  return components;
}

function connectedComponentsFromSets(groupSets: Set<string>[]): Set<string>[] {
  const adjacency = new Map<string, Set<string>>();
  const ensureNode = (designId: string) => {
    if (!adjacency.has(designId)) {
      adjacency.set(designId, new Set());
    }
  };
  for (const groupSet of groupSets) {
    const designIds = [...groupSet];
    designIds.forEach(ensureNode);
    for (const sourceDesignId of designIds) {
      for (const targetDesignId of designIds) {
        if (sourceDesignId !== targetDesignId) {
          adjacency.get(sourceDesignId)?.add(targetDesignId);
        }
      }
    }
  }
  return connectedComponentsFromAdjacency(adjacency);
}

function buildDesignGroupsForScope(
  upcs: MasterUPCData[],
  scope: ForgeDesignGroupScope,
  managedDesignIds: Set<string>
): DesiredForgeDesignGroup[] {
  const fullComponents = buildDesignGroupComponentsByScope(upcs, 'full');
  const components =
    scope === 'full' ? fullComponents : buildClosedPartialComponents(upcs, fullComponents);
  return components
    .filter((component) => [...component].some((designId) => managedDesignIds.has(designId)))
    .map((component) => ({ scope, designIds: sortIds([...component]) }));
}

function buildClosedPartialComponents(
  upcs: MasterUPCData[],
  fullComponents: Set<string>[]
): Set<string>[] {
  const fullComponentByDesignId = new Map<string, Set<string>>();
  for (const component of fullComponents) {
    for (const designId of component) {
      fullComponentByDesignId.set(designId, component);
    }
  }

  const rawPartialComponents = buildDesignGroupComponentsByScope(upcs, 'partial');
  const expandedComponents = rawPartialComponents.map((component) => {
    const expanded = new Set(component);
    for (const designId of component) {
      fullComponentByDesignId.get(designId)?.forEach((fullMemberId) => expanded.add(fullMemberId));
    }
    return expanded;
  });
  return connectedComponentsFromSets(expandedComponents);
}

function serializeDesignState(design: MasterUPCData['designs'][number]): DesiredDesignState {
  const serializedDesignId = getSerializableDesignId(design);
  const notes = getSerializableDesignNotes(design);
  const state: DesiredDesignState = {
    clientDesignId: design.id,
    _id: serializedDesignId,
    barcodeStatus: design.barcodeStatus,
    representativeCropId: design.representativeCropId || null,
    normalized_barcode: design.normalizedBarcode || null,
    cropIds: sortIds(design.crops.map((crop) => crop.id)),
  };
  if (notes.length > 0) {
    state.notes = notes;
  }
  return state;
}

function shouldSerializeEmptyPersistedDesign(design: MasterUPCData['designs'][number]): boolean {
  if (!design.persistedDesignId) return false;
  return design.barcodeStatus !== 'unconfirmed';
}

function collectNotFrontCropIds(upc: MasterUPCData): string[] {
  return sortIds([
    ...upc.designs.flatMap((design) =>
      design.crops
        .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
        .map((crop) => crop.id)
    ),
    ...(upc.pendingCrops ?? [])
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
      .map((crop) => crop.id),
    ...upc.unassignedCrops
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
      .map((crop) => crop.id),
    ...(upc.inactiveCrops ?? [])
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
      .map((crop) => crop.id),
  ]);
}

function serializeDesiredItemState(upc: MasterUPCData): DesiredItemState {
  return {
    columnKey: upc.columnKey,
    item_number: upc.actualItemNumber === undefined ? upc.itemNumber : upc.actualItemNumber,
    lookupKind: upc.lookupKind,
    lookupValue: upc.lookupValue ?? null,
    lookupNormalizedBarcode: upc.lookupNormalizedBarcode ?? null,
    snapshotVersion: upc.snapshotVersion || '',
    designs: [...upc.designs]
      .filter((design) => design.crops.length > 0 || shouldSerializeEmptyPersistedDesign(design))
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(serializeDesignState),
    pendingCropIds: sortIds((upc.pendingCrops ?? []).map((crop) => crop.id)),
    unassignedCropIds: sortIds(upc.unassignedCrops.map((crop) => crop.id)),
    notFrontCropIds: collectNotFrontCropIds(upc),
    cartonCropIds: sortIds(upc.cartonCrops.map((crop) => crop.id)),
    inactiveCropIds: sortIds((upc.inactiveCrops ?? []).map((crop) => crop.id)),
    review: buildReviewState(upc),
  };
}

export function buildMasterLabelStateSaveRequest(
  chainId: number,
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelStateSaveRequest {
  const diffSummary = computeMasterLabelDiffSummary(baseUpcs, workingUpcs);
  const dirtyItemNumbers = new Set(diffSummary.dirtyItemNumbers);
  const dirtyWorkingUpcs = workingUpcs.filter((upc) => dirtyItemNumbers.has(upc.itemNumber));
  const designGroupManagedDesignIds = collectChangedDesignGroupIds(baseUpcs, workingUpcs);
  const managedDesignIdSet = new Set(designGroupManagedDesignIds);
  return {
    chain_id: chainId,
    items: dirtyWorkingUpcs
      .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber))
      .map(serializeDesiredItemState),
    confirmedDeleteCropIds: sortIds(
      dirtyWorkingUpcs.flatMap((upc) =>
        getAllUpcCrops(upc)
          .filter((crop) => crop.markedForDeletion)
          .map((crop) => crop.id)
      )
    ),
    designGroupManagedDesignIds,
    designGroups: [
      ...buildDesignGroupsForScope(workingUpcs, 'full', managedDesignIdSet),
      ...buildDesignGroupsForScope(workingUpcs, 'partial', managedDesignIdSet),
    ],
  };
}

export function buildMasterLabelDebugInfo(
  chainId: number,
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelDebugInfo {
  const summary = computeMasterLabelDiffSummary(baseUpcs, workingUpcs);
  const request = buildMasterLabelStateSaveRequest(chainId, baseUpcs, workingUpcs);
  const basePlacements = flattenCropPlacements(baseUpcs);
  const workingPlacements = flattenCropPlacements(workingUpcs);
  const cropIds = new Set([...basePlacements.keys(), ...workingPlacements.keys()]);
  const cropChanges = [...cropIds]
    .filter(
      (cropId) =>
        cropPlacementKey(basePlacements.get(cropId)) !==
        cropPlacementKey(workingPlacements.get(cropId))
    )
    .sort((left, right) => left.localeCompare(right))
    .map((cropId) => {
      const basePlacement = basePlacements.get(cropId);
      const workingPlacement = workingPlacements.get(cropId);
      return {
        cropId,
        baseItemNumber: basePlacement?.itemNumber ?? null,
        baseSection: basePlacement?.section ?? null,
        baseDesignId: basePlacement?.designId ?? null,
        baseOrientation: basePlacement?.orientation ?? null,
        baseReviewIssueType: basePlacement?.reviewIssueType ?? null,
        baseMarkedForDeletion: basePlacement?.markedForDeletion ?? false,
        nextItemNumber: workingPlacement?.itemNumber ?? null,
        nextSection: workingPlacement?.section ?? null,
        nextDesignId: workingPlacement?.designId ?? null,
        nextOrientation: workingPlacement?.orientation ?? null,
        nextReviewIssueType: workingPlacement?.reviewIssueType ?? null,
        nextMarkedForDeletion: workingPlacement?.markedForDeletion ?? false,
      };
    });

  const basePersistedDesigns = flattenPersistedDesigns(baseUpcs);
  const workingPersistedDesigns = flattenPersistedDesigns(workingUpcs);
  const persistedDesignIds = new Set([
    ...basePersistedDesigns.keys(),
    ...workingPersistedDesigns.keys(),
  ]);
  const representativeChanges = [...persistedDesignIds]
    .map((designId) => {
      const baseDesign = basePersistedDesigns.get(designId);
      const workingDesign = workingPersistedDesigns.get(designId);
      if (!baseDesign || !workingDesign) return null;
      if (baseDesign.representativeCropId === workingDesign.representativeCropId) return null;
      return {
        designId,
        baseItemNumber: baseDesign.itemNumber,
        nextItemNumber: workingDesign.itemNumber,
        baseRepresentativeCropId: baseDesign.representativeCropId,
        nextRepresentativeCropId: workingDesign.representativeCropId,
      };
    })
    .filter((change): change is NonNullable<typeof change> => Boolean(change))
    .sort((left, right) => left.designId.localeCompare(right.designId));

  const draftDesigns = flattenDraftDesigns(workingUpcs)
    .map((design) => ({
      designId: design.id,
      itemNumber: design.itemNumber,
      representativeCropId: design.representativeCropId,
      cropIds: design.cropIds ?? [],
    }))
    .sort((left, right) => left.designId.localeCompare(right.designId));

  const designGroupChanges = collectChangedDesignGroupIds(baseUpcs, workingUpcs)
    .map((designId) => {
      const baseDesign = basePersistedDesigns.get(designId);
      const workingDesign = workingPersistedDesigns.get(designId);
      const baseFullDesign = baseDesign
        ? findPersistedDesign(baseUpcs, baseDesign.itemNumber, designId)
        : undefined;
      const workingFullDesign = workingDesign
        ? findPersistedDesign(workingUpcs, workingDesign.itemNumber, designId)
        : undefined;
      if (!baseDesign || !workingDesign || !baseFullDesign || !workingFullDesign) return null;
      return {
        designId,
        itemNumber: workingDesign.itemNumber,
        normalizedBarcode:
          workingFullDesign.normalizedBarcode ?? baseFullDesign.normalizedBarcode ?? null,
        baseFullGroupDesigns: formatDesignGroupsForDebug(baseFullDesign.fullGroupDesigns),
        nextFullGroupDesigns: formatDesignGroupsForDebug(workingFullDesign.fullGroupDesigns),
        basePartialGroupDesigns: formatDesignGroupsForDebug(baseFullDesign.partialGroupDesigns),
        nextPartialGroupDesigns: formatDesignGroupsForDebug(
          workingFullDesign.partialGroupDesigns
        ),
      };
    })
    .filter((change): change is NonNullable<typeof change> => Boolean(change))
    .sort((left, right) => left.designId.localeCompare(right.designId));

  return {
    summary,
    request,
    cropChanges,
    representativeChanges,
    draftDesigns,
    designGroupChanges,
  };
}
